import React, { useState } from 'react';
import {
  Lightning, ArrowRight, TrendDown, CurrencyEur,
  Recycle, Leaf, Truck, Factory, Timer,
  Robot, PaperPlaneRight, CircleNotch, Sparkle
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import { cbamAiAnalyze } from '../api';

const CATEGORY_ICON = {
  'Scrap Increase': Recycle,
  'Route Change': Factory,
  'Renewable Energy': Leaf,
  'Supplier Substitution': Truck,
  'Future Scope': Lightning,
};

const CATEGORY_COLOR = {
  'Scrap Increase': { bg: 'bg-teal-500/10', text: 'text-teal-400' },
  'Route Change': { bg: 'bg-orange-500/10', text: 'text-orange-400' },
  'Renewable Energy': { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  'Supplier Substitution': { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  'Future Scope': { bg: 'bg-violet-500/10', text: 'text-violet-400' },
};

const SUGGESTED_QUESTIONS = [
  "What's the cheapest way to reduce our CBAM liability by 30%?",
  "Rank all decarbonisation levers by ROI for Aluminium Housings",
  "Which suppliers are making us most expensive under CBAM?",
  "What happens if the EU adopts downstream CBAM in 2028?",
  "How can we improve our verified data coverage from 40% to 80%?",
  "Compare the CBAM impact of switching to EAF vs increasing scrap rate",
];

export default function CbamDigitalTwin({ data }) {
  const [selected, setSelected] = useState(null);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);

  if (!data) return null;
  const { scenarios, carbon_prices, emissions, products } = data;

  const activeScenario = selected !== null ? scenarios[selected] : null;

  const comparisonData = activeScenario ? [
    { name: 'Before', intensity: activeScenario.projected_intensity_before, fill: '#ef4444' },
    { name: 'After', intensity: activeScenario.projected_intensity_after, fill: '#4ade80' },
  ] : [];

  const handleAiSubmit = async (question) => {
    const q = question || aiQuestion;
    if (!q.trim()) return;
    setAiLoading(true);
    setAiQuestion('');
    setAiHistory(prev => [...prev, { role: 'user', content: q }]);
    try {
      const res = await cbamAiAnalyze(q);
      setAiHistory(prev => [...prev, { role: 'ai', content: res.data.analysis }]);
    } catch (err) {
      setAiHistory(prev => [...prev, { role: 'ai', content: `Analysis failed: ${err.response?.data?.detail || err.message}`, error: true }]);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="cbam-digital-twin">
      {/* AI Strategy Advisor */}
      <div className="bg-surface border border-primary/30 rounded-lg overflow-hidden" data-testid="cbam-ai-advisor">
        <div className="bg-primary/10 px-5 py-3 flex items-center gap-3 border-b border-primary/20">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
            <Sparkle size={18} weight="fill" className="text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-body font-semibold text-text-primary">CBAM Strategy Advisor</h3>
            <p className="text-[11px] font-body text-text-secondary">AI-powered analysis — Claude Sonnet 4.5</p>
          </div>
        </div>

        {/* Chat History */}
        {aiHistory.length > 0 && (
          <div className="max-h-[400px] overflow-y-auto p-4 space-y-4" data-testid="cbam-ai-history">
            {aiHistory.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-primary/20 text-text-primary'
                    : msg.error
                      ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                      : 'bg-white/[0.03] border border-border text-text-primary'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm font-body">{msg.content}</p>
                  ) : (
                    <div className="prose prose-sm prose-invert max-w-none text-sm font-body
                      [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-text-primary [&_h2]:mt-3 [&_h2]:mb-2
                      [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-text-primary [&_h3]:mt-2 [&_h3]:mb-1
                      [&_p]:text-text-secondary [&_p]:mb-2 [&_p]:leading-relaxed
                      [&_li]:text-text-secondary [&_li]:mb-1
                      [&_strong]:text-text-primary
                      [&_code]:text-primary [&_code]:bg-primary/10 [&_code]:px-1 [&_code]:rounded
                      [&_table]:w-full [&_table]:text-xs
                      [&_th]:text-left [&_th]:py-1.5 [&_th]:px-2 [&_th]:border-b [&_th]:border-border [&_th]:text-text-secondary [&_th]:font-semibold
                      [&_td]:py-1.5 [&_td]:px-2 [&_td]:border-b [&_td]:border-border/50 [&_td]:text-text-secondary
                    " data-testid={`cbam-ai-response-${i}`}>
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {aiLoading && (
              <div className="flex justify-start">
                <div className="bg-white/[0.03] border border-border rounded-lg px-4 py-3 flex items-center gap-2">
                  <CircleNotch size={16} className="text-primary animate-spin" />
                  <span className="text-sm font-body text-text-secondary">Analyzing CBAM data...</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Suggested Questions */}
        {aiHistory.length === 0 && (
          <div className="p-4" data-testid="cbam-ai-suggestions">
            <p className="text-xs font-body text-text-secondary mb-3">Ask anything about your CBAM exposure, or try:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  data-testid={`cbam-ai-suggestion-${i}`}
                  onClick={() => handleAiSubmit(q)}
                  disabled={aiLoading}
                  className="text-left text-xs font-body text-text-secondary bg-white/[0.02] border border-border rounded-lg px-3 py-2.5 hover:border-primary/40 hover:text-text-primary transition-all duration-200 disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-border">
          <form onSubmit={(e) => { e.preventDefault(); handleAiSubmit(); }} className="flex gap-2">
            <input
              type="text"
              data-testid="cbam-ai-input"
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              placeholder="Ask about CBAM strategy, decarbonisation ROI, supplier risk..."
              disabled={aiLoading}
              className="flex-1 bg-white/[0.03] border border-border rounded-lg px-4 py-2.5 text-sm font-body text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-primary transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="cbam-ai-submit"
              disabled={aiLoading || !aiQuestion.trim()}
              className="px-4 py-2.5 bg-primary text-white rounded-lg font-body text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              {aiLoading ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} />}
              Ask
            </button>
          </form>
        </div>
      </div>

      {/* Carbon Price Context */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-carbon-prices">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-3">Carbon Price Reference (EUR/tCO2e)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(carbon_prices || []).map((cp, i) => (
            <div key={i} className="border border-border rounded-lg p-3 text-center">
              <p className="text-lg font-heading font-bold text-text-primary">EUR {cp.price_eur_tco2e}</p>
              <p className="text-[11px] font-body font-semibold text-text-secondary uppercase tracking-wider mt-1">{cp.jurisdiction}</p>
              <p className="text-[10px] font-body text-text-secondary mt-0.5">{cp.mechanism}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Scenario List */}
        <div className="lg:col-span-2 space-y-2" data-testid="cbam-scenario-list">
          <h3 className="text-sm font-body font-semibold text-text-primary mb-2">Pre-Built What-If Scenarios</h3>
          {(scenarios || []).map((sc, i) => {
            const CatIcon = CATEGORY_ICON[sc.category] || Lightning;
            const catColor = CATEGORY_COLOR[sc.category] || CATEGORY_COLOR['Future Scope'];
            const isActive = selected === i;
            return (
              <button
                key={i}
                data-testid={`cbam-scenario-${sc.scenario_id}`}
                onClick={() => setSelected(isActive ? null : i)}
                className={`w-full text-left border rounded-lg p-4 transition-all duration-200 ${isActive ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-tertiary/40'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg ${catColor.bg} flex items-center justify-center shrink-0`}>
                    <CatIcon size={16} weight="duotone" className={catColor.text} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-body font-semibold text-text-primary">{sc.title}</p>
                    <p className="text-[10px] font-body text-text-secondary mt-0.5">{sc.category}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs font-body">
                      {sc.intensity_reduction_pct > 0 && (
                        <span className="text-emerald-400 font-semibold flex items-center gap-1">
                          <TrendDown size={12} />-{sc.intensity_reduction_pct}%
                        </span>
                      )}
                      {sc.annual_cbam_saving_eur > 0 && (
                        <span className="text-teal-400 font-semibold">
                          Save EUR {(sc.annual_cbam_saving_eur / 1000).toFixed(0)}K/yr
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Scenario Detail */}
        <div className="lg:col-span-3" data-testid="cbam-scenario-detail">
          {activeScenario ? (
            <div className="bg-surface border border-border rounded-lg p-5 space-y-5">
              <div>
                <h3 className="text-base font-body font-semibold text-text-primary">{activeScenario.title}</h3>
                <p className="text-xs font-body text-text-secondary mt-1">{activeScenario.description}</p>
              </div>

              {/* Parameter Changes */}
              <div className="border border-border rounded-lg p-4">
                <h4 className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider mb-3">Parameter Changes</h4>
                <div className="space-y-2">
                  {Object.entries(activeScenario.parameter_changes || {}).map(([key, val], i) => (
                    <div key={i} className="flex items-center gap-2 text-sm font-body">
                      <span className="text-text-secondary capitalize">{key.replace(/_/g, ' ')}:</span>
                      <span className="text-red-400 font-mono">{typeof val.from === 'number' ? val.from : String(val.from)}</span>
                      <ArrowRight size={14} className="text-text-secondary" />
                      <span className="text-emerald-400 font-mono">{typeof val.to === 'number' ? val.to : String(val.to)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Before/After Chart */}
              {comparisonData.length > 0 && activeScenario.projected_intensity_before > 0 && (
                <div>
                  <h4 className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider mb-3">Emissions Intensity (tCO2e/t)</h4>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={comparisonData} margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                      <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`${v} tCO2e/t`, 'Intensity']} />
                      <Bar dataKey="intensity" radius={[4, 4, 0, 0]} barSize={50}>
                        {comparisonData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Impact Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border border-border rounded-lg p-3 text-center">
                  <p className="text-xs font-body text-text-secondary">Intensity Reduction</p>
                  <p className="text-lg font-heading font-bold text-emerald-400">{activeScenario.intensity_reduction_pct > 0 ? `-${activeScenario.intensity_reduction_pct}%` : 'N/A'}</p>
                </div>
                <div className="border border-border rounded-lg p-3 text-center">
                  <p className="text-xs font-body text-text-secondary">Annual Saving</p>
                  <p className="text-lg font-heading font-bold text-teal-400">{activeScenario.annual_cbam_saving_eur > 0 ? `EUR ${(activeScenario.annual_cbam_saving_eur / 1000).toFixed(0)}K` : 'N/A'}</p>
                </div>
                <div className="border border-border rounded-lg p-3 text-center">
                  <p className="text-xs font-body text-text-secondary">Investment</p>
                  <p className="text-lg font-heading font-bold text-amber-400">{activeScenario.investment_required_eur > 0 ? `EUR ${(activeScenario.investment_required_eur / 1000).toFixed(0)}K` : 'None'}</p>
                </div>
                <div className="border border-border rounded-lg p-3 text-center">
                  <p className="text-xs font-body text-text-secondary">Payback</p>
                  <p className="text-lg font-heading font-bold text-text-primary">{activeScenario.payback_months != null ? `${activeScenario.payback_months} mo` : 'N/A'}</p>
                </div>
              </div>

              {/* AI Deep Dive button */}
              <button
                data-testid="cbam-ai-deep-dive"
                onClick={() => handleAiSubmit(`Provide a detailed analysis of this scenario: "${activeScenario.title}". Include implementation steps, risks, timeline, and compare with alternative approaches.`)}
                disabled={aiLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary/10 border border-primary/30 text-primary rounded-lg text-sm font-body font-medium hover:bg-primary/20 transition-colors disabled:opacity-40"
              >
                <Sparkle size={16} weight="fill" />
                Deep Dive with AI
              </button>
            </div>
          ) : (
            <div className="bg-surface border border-border rounded-lg p-10 flex flex-col items-center justify-center text-center h-full min-h-[300px]">
              <Lightning size={40} weight="duotone" className="text-tertiary mb-4" />
              <p className="text-sm font-body font-semibold text-text-primary">Select a scenario</p>
              <p className="text-xs font-body text-text-secondary mt-1 max-w-xs">
                Choose a what-if scenario to simulate the impact on CBAM emissions intensity, liability, and payback.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* All Scenarios Summary Table */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-scenario-summary">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Scenario Comparison Summary</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-semibold">Scenario</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Category</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Before</th>
                <th className="text-right py-2 text-text-secondary font-semibold">After</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Reduction</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Saving /yr</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Investment</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Payback</th>
              </tr>
            </thead>
            <tbody>
              {(scenarios || []).map((sc, i) => (
                <tr key={i} className={`border-b border-border/50 hover:bg-white/[0.02] cursor-pointer ${selected === i ? 'bg-primary/5' : ''}`} onClick={() => setSelected(i)}>
                  <td className="py-2.5 text-text-primary font-medium max-w-[200px] truncate">{sc.title}</td>
                  <td className="py-2.5"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${(CATEGORY_COLOR[sc.category] || {}).bg || ''} ${(CATEGORY_COLOR[sc.category] || {}).text || ''}`}>{sc.category}</span></td>
                  <td className="py-2.5 text-right text-text-secondary">{sc.projected_intensity_before || '-'}</td>
                  <td className="py-2.5 text-right text-text-primary font-semibold">{sc.projected_intensity_after || '-'}</td>
                  <td className="py-2.5 text-right text-emerald-400 font-semibold">{sc.intensity_reduction_pct > 0 ? `-${sc.intensity_reduction_pct}%` : '-'}</td>
                  <td className="py-2.5 text-right text-teal-400 font-semibold">{sc.annual_cbam_saving_eur > 0 ? `EUR ${(sc.annual_cbam_saving_eur / 1000).toFixed(0)}K` : '-'}</td>
                  <td className="py-2.5 text-right text-text-secondary">{sc.investment_required_eur > 0 ? `EUR ${(sc.investment_required_eur / 1000).toFixed(0)}K` : '-'}</td>
                  <td className="py-2.5 text-right text-text-secondary">{sc.payback_months != null ? `${sc.payback_months} mo` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
